import AudienceSearch from "@/modules/marketing/audience/AudienceSearch.model";
import MarketingService from "@/modules/marketing/MarketingService";
import _ from "lodash";
import {MARKETING_TABLE_NAME, SOURCE_AUDIENCES_LIST, SOURCE_CAMPAIGNS_LIST} from "@/modules/constants";
import i18n from "../../../shared/i18n";
import {store} from "@/plugins/vuex";

let getDefaultTableData = (differentFields = []) => {
  return {
    loading: false,
    rows: [],
    pagination: {
      limit: 10,
      page: 1,
      total: 0,
    },
    ordering: '-created_at',
    search: '',
    searchModel: new AudienceSearch(),
    expand: '',
    toggleColumns: {
      fields: [...getDefaultFields(), ...differentFields],
      requiredFields: ['total_customers'],
      defaultFields: ['name', 'subject', 'total_customers', 'unsubscribed_customers', 'created_at', 'created_by'],
    }
  }
};

let getDefaultFields = () => {
  return [
    {key: 'name', label: i18n.t('Audience'), sortable: true, isVisible: true, expand: ''},
    {key: 'subject', label: i18n.t('Subject'), sortable: true, isVisible: true, expand: ''},
    {key: 'total_customers', label: i18n.t('Subscribers'), sortable: true, isVisible: true, expand: 'total_customers'},
    {key: 'unsubscribed_customers', label: i18n.t('Unsubscribed'), sortable: true, isVisible: true, expand: 'unsubscribed_customers'},
    {key: 'created_at', label: i18n.t('Created At'), sortable: true, isVisible: true, expand: ''},
    {key: 'created_by', label: i18n.t('Created by'), sortable: true, isVisible: true, expand: 'created_by'},
  ]
}

let getCampaignDefaultTableData = () => {
  return {
    loading: false,
    rows: [],
    pagination: {
      limit: 50,
      page: 1,
      total: 0,
    },
    ordering: '-created_at',
    search: '',
  }
}

const audience = {
  namespaced: true,
  state: {
    tableData: getDefaultTableData(),
    audienceModal: {
      show: false,
      object: null,
      source: null
    },
    view: {
      loading: false,
      audience: null,
      campaignTableData: getCampaignDefaultTableData(),
      campaignModal: {
        show: false,
        object: null,
        source: null
      },
      audienceCustomersModal: {
        show: false,
        source: null
      },
    }
  },
  actions: {
    resetTableDataPaginationPage({state}) {
      state.tableData.pagination.page = 1;
    },
    updateSearchModel({commit, state}, searchModel) {
      commit('updateSearchModel', new AudienceSearch({
        ...state.tableData.searchModel,
        ...searchModel
      }));
    },
    async getTableParams({state}, payload = null) {
      let data;
      const ordering = await store.dispatch('global/getTableDataOrdering', {
        tableData: state.tableData,
        tableName: MARKETING_TABLE_NAME
      });

      if (payload) {
        data = {
          ...payload.pagination,
          search: payload.search,
          ordering: payload.ordering,
        };
      } else {
        data = {
          ...state.tableData.pagination,
          search: state.tableData.search,
          ordering: ordering,
        }
      }

      if (state.tableData.searchModel) {
        const searchModelData = state.tableData.searchModel.toJSON();
        data = {
          ...data,
          ...searchModelData
        }
      }

      const defaultExpand = state.tableData.expand
      const expand = state.tableData.toggleColumns.fields
          .filter(c => state.tableData.toggleColumns.defaultFields.includes(c.key) && c.expand && !defaultExpand.includes(c.expand))
          .map(c => c.expand)
          .join(',')

      data.expand = `${expand},${defaultExpand}`;

      return data;
    },
    async getTableData({commit, dispatch}, payload) {
      let params = await dispatch('getTableParams', payload);

      commit('updateTableLoading', true);
      const response = await MarketingService.getAudiences(params);
      commit('updateTableLoading', false);

      if (response.success) {
        commit('updateTableData', {
          rows: response.body.results,
          pagination: {limit: params.limit, page: params.page, total: response.body.count},
          ordering: params.ordering,
          search: params.search
        });
      }

      return response;
    },
    async resetTable({state, dispatch}) {
      const tmpToggleColumns = _.cloneDeep(state.tableData.toggleColumns);
      let defaultTableData = getDefaultTableData();
      defaultTableData.pagination.limit = state.tableData.pagination.limit;
      state.tableData = defaultTableData;
      state.tableData.toggleColumns = tmpToggleColumns;
      dispatch('getTableData');
    },
    showAudienceModal({commit}, {object = {}, source} = {}) {
      commit('showAudienceModal', {object: _.cloneDeep(object), source});
    },
    hideAudienceModal({commit}) {
      commit('hideAudienceModal');
    },
    showAudienceCustomersModal({commit}, {source} = {}) {
      commit('showAudienceCustomersModal', {source});
    },
    hideAudienceCustomersModal({commit}) {
      commit('hideAudienceCustomersModal');
    },
    async updateAudience({commit, state}, audience) {
      let response = await MarketingService.updateAudience(audience.uuid, audience);
      if (response.success && state.audienceModal.source === SOURCE_AUDIENCES_LIST) {
        commit('updateListAudience', response.body)
      }
      return response;
    },
    async removeAudience({dispatch}, audience) {
      let response = await MarketingService.removeAudience(audience.uuid);
      if (response.success) {
        dispatch('getTableData')
      }
      return response;
    },
    async getAudience({commit}, uuid) {
      commit('changeCurrentAudienceLoading');
      let response = await MarketingService.getAudience(uuid, 'customers,created_by');
      commit('changeCurrentAudienceLoading');

      if (response.success) {
        commit('updateViewAudience', response.body);
      }
      return response;
    },
    async updateViewAudience({commit}, audience) {
      commit('updateViewAudience', audience);
    },
    async getCampaignTableData({commit, state}, payload) {
      let params;

      if (payload) {
        params = {
          ...payload.pagination,
          search: payload.search,
          ordering: payload.ordering,
        };
      } else {
        params = {
          ...state.view.campaignTableData.pagination,
          search: state.view.campaignTableData.search,
          ordering: state.view.campaignTableData.ordering,
        };
      }

      commit('updateCampaignTableLoading', true);
      const response = await MarketingService.getCampaigns(params);
      commit('updateCampaignTableLoading', false);

      if (response.success) {
        commit('updateCampaignTableData', {
          rows: response.body.results,
          pagination: {limit: params.limit, page: params.page, total: response.body.count},
          ordering: params.ordering,
          search: params.search
        });
      }

      return response;
    },
    showCampaignModal({commit}, {object = {}, source} = {}) {
      commit('showCampaignModal', {object: _.cloneDeep(object), source});
    },
    hideCampaignModal({commit}) {
      commit('hideCampaignModal');
    },
    async createCampaign({dispatch}, campaign) {
      let response = await MarketingService.createCampaign(campaign);
      if (response.success) {
        dispatch('getCampaignTableData');
      }
      return response;
    },
    async updateCampaign({commit, state}, campaign) {
      let response = await MarketingService.updateCampaign(campaign.uuid, campaign);
      if (response.success && state.view.campaignModal.source === SOURCE_CAMPAIGNS_LIST) {
        commit('updateListCampaign', response.body)
      }
      return response;
    },
    async getModalObject({commit}, {uuid, source}) {
      let response = await MarketingService.getAudience(uuid, 'customers,created_by');

      if (response.success) {
        commit('updateModalObject', {object: _.cloneDeep(response.body), source})
      }
    },
    async updateToggleColumns({commit, dispatch}, payload) {
      if (payload.isSaveAction) {
        commit('updateTableLoading', true);
        await commit('updateToggleColumns', payload.selectedColumns);
        await dispatch('getTableData');
        commit('updateTableLoading', false);
      } else {
        await commit('updateToggleColumns', payload.selectedColumns);
      }
    },
    async unsubscribeCustomersFromAudience({state}, customer_uuids) {
      customer_uuids = Array.from(customer_uuids).map(item => item.uuid);
      return await MarketingService.unsubscribeCustomersFromAudience(state.view.audience.uuid, {customer_uuids});
    },
    async removeCustomersFromAudience({state}, customer_uuids) {
      customer_uuids = Array.from(customer_uuids).map(item => item.uuid);
      return await MarketingService.removeCustomersFromAudience(state.view.audience.uuid, {customer_uuids});
    }
  },
  mutations: {
    updateTableData: (state, payload) => state.tableData = {...state.tableData, ...payload},
    updateSearchModel: (state, searchModel) => state.tableData.searchModel = searchModel,
    updateTableLoading: (state, payload) => state.tableData.loading = payload,
    showAudienceModal: (state, {object, source}) => {
      state.audienceModal.show = true;
      state.audienceModal.object = object;
      state.audienceModal.source = source;
    },
    hideAudienceModal: state => state.audienceModal.show = false,
    showAudienceCustomersModal: (state, {source}) => {
      state.view.audienceCustomersModal.show = true;
      state.view.audienceCustomersModal.source = source;
    },
    hideAudienceCustomersModal: state => state.view.audienceCustomersModal.show = false,
    changeCurrentAudienceLoading: state => state.view.loading = !state.view.loading,
    updateViewAudience: (state, audience) => state.view.audience = {
      ...state.view.audience,
      ...audience
    },
    updateListAudience: (state, audience) => {
      const index = state.tableData.rows.findIndex(r => r.uuid === audience.uuid);
      state.tableData.rows[index] = {...state.tableData.rows[index], ...audience};
      state.tableData.rows = [...state.tableData.rows];
    },
    updateCampaignTableData: (state, payload) => state.view.campaignTableData = {...state.view.campaignTableData, ...payload},
    showCampaignModal: (state, {object, source}) => {
      state.view.campaignModal.show = true;
      state.view.campaignModal.object = object;
      state.view.campaignModal.source = source;
    },
    hideCampaignModal: state => state.view.campaignModal.show = false,
    updateCampaignTableLoading: (state, payload) => state.view.campaignTableData.loading = payload,
    updateListCampaign: (state, campaign) => {
      const index = state.view.campaignTableData.rows.findIndex(r => r.uuid === campaign.uuid);
      state.view.campaignTableData.rows[index] = {...state.view.campaignTableData.rows[index], ...campaign};
      state.view.campaignTableData.rows = [...state.view.campaignTableData.rows];
    },
    updateModalObject: (state, {object, source}) => {
      state.audienceModal.object = object;
      state.audienceModal.source = source;
    },
    updateToggleColumns: (state, selectedColumns) => {
      state.tableData.toggleColumns.defaultFields = [...selectedColumns];
      let selectedFields = [];
      let notSelectedFields = [];
      selectedColumns.forEach(s => {
        selectedFields.push({
          ...state.tableData.toggleColumns.fields.find(f => f.key === s),
          isVisible: true
        });
      });
      state.tableData.toggleColumns.fields.forEach(f => {
        if (!selectedColumns.includes(f.key)) {
          notSelectedFields.push({...f, isVisible: false});
        }
      });
      state.tableData.toggleColumns.fields = [...selectedFields, ...notSelectedFields];
    },
  }
}

export default audience;
